import { createSelector } from 'reselect';
import { flow, pipe, identity } from 'fp-ts/function';
import { array, nonEmptyArray, option, record, string } from 'fp-ts';
import { getStringProp, primitiveTypeUniq } from '@mews/fp';
import { Reservation } from '@mews-commander/data-access';
import { getReservationPersonCount } from '@mews-commander/legacy-entities/src/utils/reservations';
import { getResourceCategoryAssignmentIdMappingByServiceIdByResourceIdSelector } from '@mews-commander/legacy-entities/src/ducks/resourceCategoryAssignments';
import { getSlots } from '@mews-commander/legacy-entities/src/ducks/resourceSlots';
import { getDefaultProductOrderItemMapByServiceOrderId } from '@mews-commander/legacy-entities/src/selectors/orderItems';
import { getServiceSelectedReservationsCategoryIds } from './summary';
import { getServiceCheckedReservations } from './reservationModuleState';

export const getSelectedReservationsUniqueCategoryIds = flow(
    getServiceSelectedReservationsCategoryIds,
    primitiveTypeUniq
);

export interface Entry {
    date: string;
    personCount: number;
    resourceCount: number;
    categoryCountMap: Record<string, number>;
}

const getUnassignedReservationsCategoryIds = array.filterMap((r: Reservation) =>
    !r.assignedResourceId && !r.assignedSlotId ? option.some(r.requestedCategoryId) : option.none
);
export const getResourceTableSource = createSelector(
    getServiceCheckedReservations,
    getStringProp('serviceId'),
    getDefaultProductOrderItemMapByServiceOrderId,
    getSlots,
    getResourceCategoryAssignmentIdMappingByServiceIdByResourceIdSelector,
    (reservations, serviceId, defaultProductOrderMap, slots, resourceCategoryAssigMap) => {
        const dateReservationsMap = reservations.reduce(
            (acc, reservation) => {
                const reservationOrderItems = defaultProductOrderMap[reservation.id];
                if (reservationOrderItems) {
                    const startUtcs = reservationOrderItems.map(({ startUtc }) => startUtc ?? '');
                    startUtcs.forEach((startUtc) => {
                        acc[startUtc] ??= [];
                        acc[startUtc].push(reservation);
                    });
                }
                return acc;
            },
            {} as Record<string, Reservation[]>
        );

        return pipe(
            dateReservationsMap,
            record.collect(string.Ord)((date, sameDateReservations): Entry => {
                const slotCategoryIds = pipe(
                    sameDateReservations,
                    array.filterMap((r) =>
                        !r.assignedResourceId && r.assignedSlotId ? option.some(r.assignedSlotId) : option.none
                    ),
                    array.map((slotId) => slots[slotId]?.categoryId)
                );
                const assignedResourceCategoryIds = pipe(
                    sameDateReservations,
                    array.filterMap(option.fromNullableK((r) => r.assignedResourceId)),
                    array.filterMap(
                        option.fromNullableK(
                            (resourceId) => resourceCategoryAssigMap(serviceId, resourceId)?.categoryId
                        )
                    )
                );
                const unassignedReservationsCategoryIds = getUnassignedReservationsCategoryIds(sameDateReservations);
                const resourceCategoryIds = [
                    ...slotCategoryIds,
                    ...assignedResourceCategoryIds,
                    ...unassignedReservationsCategoryIds,
                ];
                const categoryCountMap = pipe(
                    resourceCategoryIds,
                    nonEmptyArray.groupBy(identity),
                    record.map(array.size)
                );
                return {
                    date,
                    personCount: sameDateReservations.reduce(
                        (acc, reservation) => acc + getReservationPersonCount(reservation),
                        0
                    ),
                    resourceCount: sameDateReservations.length,
                    categoryCountMap,
                };
            })
        );
    }
);
